// 로컬 스토리지 유틸리티 함수들

/**
 * 로컬 스토리지에 데이터 저장
 * @param {string} key - 저장할 키
 * @param {any} value - 저장할 값
 * @param {number} ttl - 만료 시간 (밀리초, 선택사항)
 */
export const setStorageItem = (key, value, ttl = null) => {
  try {
    const item = {
      value,
      timestamp: Date.now(),
      ...(ttl && { expiresAt: Date.now() + ttl }),
    };
    
    localStorage.setItem(key, JSON.stringify(item));
    return true;
  } catch (error) {
    console.error('스토리지 저장 오류:', error);
    return false;
  }
};

/**
 * 로컬 스토리지에서 데이터 조회
 * @param {string} key - 조회할 키
 * @param {any} defaultValue - 기본값
 * @returns {any} 저장된 값 또는 기본값
 */
export const getStorageItem = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    
    if (!item) {
      return defaultValue;
    }
    
    const parsedItem = JSON.parse(item);
    
    // 만료 시간 확인
    if (parsedItem.expiresAt && Date.now() > parsedItem.expiresAt) {
      localStorage.removeItem(key);
      return defaultValue;
    }
    
    return parsedItem.value;
  } catch (error) {
    console.error('스토리지 조회 오류:', error);
    return defaultValue;
  }
};

/**
 * 로컬 스토리지에서 데이터 삭제
 * @param {string} key - 삭제할 키
 */
export const removeStorageItem = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('스토리지 삭제 오류:', error);
    return false;
  }
};

/**
 * 로컬 스토리지 전체 삭제
 */
export const clearStorage = () => {
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    console.error('스토리지 전체 삭제 오류:', error);
    return false;
  }
};

/**
 * 로컬 스토리지에 키가 존재하는지 확인
 * @param {string} key - 확인할 키
 * @returns {boolean} 존재 여부
 */
export const hasStorageItem = (key) => {
  try {
    return localStorage.getItem(key) !== null;
  } catch (error) {
    console.error('스토리지 키 확인 오류:', error);
    return false;
  }
};

/**
 * 로컬 스토리지의 모든 키 조회
 * @returns {string[]} 키 배열
 */
export const getStorageKeys = () => {
  try {
    return Object.keys(localStorage);
  } catch (error) {
    console.error('스토리지 키 조회 오류:', error);
    return [];
  }
};

/**
 * 로컬 스토리지 사용량 확인 (대략적)
 * @returns {number} 사용량 (바이트)
 */
export const getStorageSize = () => {
  try {
    let total = 0;
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        total += localStorage[key].length + key.length;
      }
    }
    return total;
  } catch (error) {
    console.error('스토리지 크기 확인 오류:', error);
    return 0;
  }
};

/**
 * 만료된 스토리지 항목들 정리
 */
export const cleanupExpiredStorage = () => {
  try {
    const keys = getStorageKeys();
    let cleanedCount = 0;
    
    keys.forEach(key => {
      const item = getStorageItem(key);
      if (item === null) {
        // 만료된 항목은 이미 삭제됨
        cleanedCount++;
      }
    });
    
    return cleanedCount;
  } catch (error) {
    console.error('스토리지 정리 오류:', error);
    return 0;
  }
};

// 세션 스토리지 유틸리티 (로컬 스토리지와 동일한 인터페이스)
export const sessionStorage = {
  setItem: (key, value, ttl = null) => {
    try {
      const item = {
        value,
        timestamp: Date.now(),
        ...(ttl && { expiresAt: Date.now() + ttl }),
      };
      
      window.sessionStorage.setItem(key, JSON.stringify(item));
      return true;
    } catch (error) {
      console.error('세션 스토리지 저장 오류:', error);
      return false;
    }
  },

  getItem: (key, defaultValue = null) => {
    try {
      const item = window.sessionStorage.getItem(key);
      
      if (!item) {
        return defaultValue;
      }
      
      const parsedItem = JSON.parse(item);
      
      if (parsedItem.expiresAt && Date.now() > parsedItem.expiresAt) {
        window.sessionStorage.removeItem(key);
        return defaultValue;
      }
      
      return parsedItem.value;
    } catch (error) {
      console.error('세션 스토리지 조회 오류:', error);
      return defaultValue;
    }
  },

  removeItem: (key) => {
    try {
      window.sessionStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('세션 스토리지 삭제 오류:', error);
      return false;
    }
  },

  clear: () => {
    try {
      window.sessionStorage.clear();
      return true;
    } catch (error) {
      console.error('세션 스토리지 전체 삭제 오류:', error);
      return false;
    }
  },

  hasItem: (key) => {
    try {
      return window.sessionStorage.getItem(key) !== null;
    } catch (error) {
      console.error('세션 스토리지 키 확인 오류:', error);
      return false;
    }
  },

  getKeys: () => {
    try {
      return Object.keys(window.sessionStorage);
    } catch (error) {
      console.error('세션 스토리지 키 조회 오류:', error);
      return [];
    }
  },
};

export default {
  setItem: setStorageItem,
  getItem: getStorageItem,
  removeItem: removeStorageItem,
  clear: clearStorage,
  hasItem: hasStorageItem,
  getKeys: getStorageKeys,
  getSize: getStorageSize,
  cleanup: cleanupExpiredStorage,
  session: sessionStorage,
}; 